import { useQueries } from '@tanstack/react-query'
import { IMenuOption } from 'layouts/default'
import { PropsWithChildren, createContext, useContext, useState } from 'react'
import { load } from 'services/requests'
import { useAuth } from './auth'

interface IOrganizationOption {
  id: string
  organizationTradingName: string
  invCorporateName: string
  federalTaxId: string
  corporateName: string
  tradingName: string
  blocked: boolean
  isInactive: boolean
  createdAt: Date
  logo: string
  responsibleEmail: string
  responsibleName: string
  token: string
  inviteId: string
  status: string
}

interface SidebarContextType {
  expanded: boolean
  expandedSubMenu: boolean
  isLoading: boolean
  menuOptions: IMenuOption[]
  organizationOptions: IOrganizationOption[]
  selectedSubMenu: IMenuOption
  previusSelectedSubMenu: IMenuOption
  onExpand: () => void
  onExpandSub: () => void
  onCloseAll: () => void
  onSelectSubMenuOption: (idn: string) => void
  onsSelectPreviusSubMenuOption: (idn: string) => void
  onReloadMenu: () => Promise<void>
}

const SidebarContext = createContext({} as SidebarContextType)

const SidebarProvider = ({ children }: PropsWithChildren) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  const [expandedSubMenu, setExpandedSubMenu] = useState<boolean>(false)
  const [selectedMenuOption, setSelectedMenuOption] = useState<IMenuOption>({} as IMenuOption)
  const [previusSelectedSubMenu, setPreviusSelectedSubMenu] = useState<IMenuOption>({} as IMenuOption)

  const { isAuthenticated } = useAuth()

  const [menu, organizations] = useQueries({
    queries: [
      {
        queryKey: ['users-security', 'get-menu'],
        queryFn: () => load('/users-security/get-menu'),
        enabled: isAuthenticated,
      },
      {
        queryKey: ['dashboard', 'organizationOptions'],
        queryFn: () =>
          load('/organizations/list', {
            columnOrder: [],
            page: 0,
            rowsPerPage: 100,
            search: '',
            status: '',
            organizationId: '',
          }),
        enabled: isAuthenticated,
      },
    ],
  })

  const isLoading = menu.isLoading || organizations.isLoading
  const isPending = organizations.isLoading || menu.isLoading

  const userMenu: IMenuOption[] = menu.data?.data.data || []
  const organizationOptions: IOrganizationOption[] = organizations.data?.data.data || []

  const handleExpand = () => {
    if (expandedSubMenu && expanded) {
      return
    } else {
      setExpanded((prev) => !prev)
    }
  }

  const handleExpandSub = () => {
    setExpandedSubMenu((prev) => !prev)
  }

  const handleCloseAll = () => {
    setExpanded(false)
    setExpandedSubMenu(false)
    setSelectedMenuOption({} as IMenuOption)
  }

  const handleSelectSubMenuOption = (idn: string) => {
    const option = userMenu.find((menuOption) => menuOption.id === idn)

    if (option) {
      setSelectedMenuOption(option)
      setPreviusSelectedSubMenu(selectedMenuOption)
    }
  }

  const handleRefetch = async () => {
    await Promise.all([menu.refetch(), organizations.refetch()])
  }

  return (
    <SidebarContext.Provider
      value={{
        expanded,
        expandedSubMenu,
        selectedSubMenu: selectedMenuOption,
        previusSelectedSubMenu,
        menuOptions: userMenu,
        organizationOptions,
        onExpand: handleExpand,
        onExpandSub: handleExpandSub,
        onCloseAll: handleCloseAll,
        onSelectSubMenuOption: handleSelectSubMenuOption,
        onsSelectPreviusSubMenuOption: handleSelectSubMenuOption,
        isLoading: isLoading || isPending,
        onReloadMenu: handleRefetch,
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

const useSidebar = () => useContext(SidebarContext)

export { SidebarProvider, useSidebar }
